import { I18nLocale, SUPPORTED_LOCALES } from "@outschool/localization";
import isNil from "lodash/isNil";
import omit from "lodash/omit";
import slugify from "slugify";

export interface UrlParams {
  [key: string]: string | undefined | boolean | null | number;
}

export interface ActivitySlugProps {
  slug_id?: string | null;
  title?: string | null;
  is_club?: boolean;
  isClub?: boolean;
  uid: string;
}

export function activitySlug(activity: ActivitySlugProps, noTitle = false) {
  if (activity.slug_id) {
    if (noTitle) {
      return activity.slug_id;
    }
    const title = activity.title ? activity.title.toLowerCase() : "";
    const cleanTitle = slugify(title.replace(/\([^)]*\)/g, ""))
      .replace(/[!'",:.]/g, "")
      .replace(/--/g, "-")
      .replace(/-$/g, "");
    return `${cleanTitle}-${activity.slug_id}`;
  }
  return activity.uid || activity;
}

/**
 *  used for example to add utm params to a url generated by Routes file
 */
export function addParamsToUrl(url: string, params: UrlParams) {
  const urlParams = queryStringToObject(hrefToQueryString(url));
  const newParams = { ...urlParams, ...params };
  const hasParams =
    Object.keys(newParams).filter(k => Boolean(newParams[k])).length > 0;
  if (!hasParams) {
    return url.split("?")[0];
  }
  return url.split("?")[0] + "?" + objectToQueryString(newParams);
}

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign: string;
}

export function addMissingUtmParams(
  url: string,
  {
    utm_source = "transactional",
    utm_medium = "email",
    utm_campaign,
  }: UtmParams,
) {
  const RELATIVE_URL_BASE = "http://localhost"; // This is thrown away at the end
  const parsedUrl = new URL(url, RELATIVE_URL_BASE);
  const existingUrlParams = queryStringToObject(parsedUrl.search);
  const updatedParams = {
    utm_source,
    utm_medium,
    utm_campaign,
    ...existingUrlParams, // let url params to override utm_ params, if any
  };
  parsedUrl.search = objectToQueryString(updatedParams);
  const isAbsolute = /^https?:\/\//.test(url);
  return isAbsolute
    ? parsedUrl.toString()
    : parsedUrl.pathname + parsedUrl.search + parsedUrl.hash;
}

/**
 * Parses the `t` query value (seek offset in whole seconds), e.g. from
 * `new URLSearchParams(location.search).get("t")`.
 */
export function parseRecordingSeekParam(
  t: string | null | undefined,
): number | undefined {
  if (t == null || t === "") {
    return undefined;
  }
  const n = Number(t);
  if (!Number.isFinite(n) || n < 0) {
    return undefined;
  }
  return Math.floor(n);
}

/**
 * Returns the leading decimal number of one `:`-separated timestamp segment
 * (`"11.150"` → 11.15, `"11.??"` → 11), or undefined when the segment has no
 * leading digits (`"??"`).
 */
function parseVideoTimestampSegment(segment: string): number | undefined {
  const match = /^(\d+(?:\.\d+)?)/.exec(segment.trim());
  if (!match) {
    return undefined;
  }
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : undefined;
}

/**
 * Converts a class-recording insight / Mux seek timestamp to seconds.
 *
 * Accepts `HH:MM:SS`, `HH:MM:SS.ms`, or `HH:MM:SS:ff`; segments after seconds
 * (frames) are ignored, and trailing junk on a segment is dropped
 * (`"00:42:11.??"` → 2531). Returns a finite number, or undefined when H, M,
 * or S has no leading digits.
 */
export function videoTimestampToSeconds(
  videoTimestamp: string | null | undefined,
): number | undefined {
  if (!videoTimestamp) {
    return undefined;
  }
  const parts = videoTimestamp.split(":").map(parseVideoTimestampSegment);
  if (parts.length < 3) {
    return undefined;
  }
  const [h, m, s] = parts;
  if (h === undefined || m === undefined || s === undefined) {
    return undefined;
  }
  return h * 3600 + m * 60 + s;
}

/**
 * Formats class-recording `videoStartTimestamp` (e.g. HH:MM:SS:ff) for UI labels.
 * Under one hour: "M:SS" or "MM:SS" (e.g. "39:15"). One hour or more: "H:MM:SS"
 * (e.g. "1:06:20"), not total minutes (e.g. not "66:20").
 */
export function formatVideoStartTimestampForDisplay(
  videoStartTimestamp: string | null | undefined,
): string | null {
  if (!videoStartTimestamp) {
    return null;
  }
  const parts = videoStartTimestamp.split(/[.:]/).map(Number);
  if (parts.length === 4) {
    const h = parts[0] ?? 0;
    const m = parts[1] ?? 0;
    const s = parts[2] ?? 0;
    const totalSeconds = h * 3600 + m * 60 + s;
    if (totalSeconds >= 3600) {
      const hours = Math.floor(totalSeconds / 3600);
      const remainderAfterHours = totalSeconds % 3600;
      const minutes = Math.floor(remainderAfterHours / 60);
      const seconds = remainderAfterHours % 60;
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(
        seconds,
      ).padStart(2, "0")}`;
    }
    const mm = h * 60 + m;
    const ss = String(s).padStart(2, "0");
    return `${mm}:${ss}`;
  }
  return videoStartTimestamp;
}

export function hrefToQueryString(s: string) {
  return (s && s.split("?")[1]) || "";
}

export function objectToQueryString(obj: UrlParams, questionMark?: boolean) {
  return (
    (questionMark ? "?" : "") +
    Object.keys(obj)
      .filter(k => !isNil(obj[k]))
      .map(function (k) {
        var keyValue = encodeURIComponent(k);
        const value = obj[k];
        if (value !== undefined && value !== null) {
          keyValue = keyValue + "=" + encodeURIComponent(value);
        }
        return keyValue;
      })
      .join("&")
  );
}

export function queryStringToObject(qs: string, parseValueType = false) {
  const params: Record<string, any> = {};
  const withoutQuestionMark = qs.startsWith("?") ? qs.slice(1) : qs;
  // filter is used to remove the empty string
  const paramsArray = withoutQuestionMark.split("&").filter(s => s);

  paramsArray.forEach(function (e) {
    const kv = e.split("=");
    const key = decodeURIComponent(kv[0]);
    const value = decodeURIComponent(kv[1]);

    if (parseValueType) {
      let parsedValue;
      if (!isNaN(Number(value))) {
        parsedValue = Number(value);
      } else if (value === "true" || value === "false") {
        parsedValue = value === "true";
      } else {
        parsedValue = value;
      }
      params[key] = parsedValue;
    } else {
      params[key] = value;
    }
  });

  return params;
}

export function fuseQueryParams(
  query1: string | UrlParams | null | undefined,
  query2: string | UrlParams | null | undefined,
  questionMark?: boolean,
) {
  const obj1 = typeof query1 == "string" ? queryStringToObject(query1) : query1;
  const obj2 = typeof query2 == "string" ? queryStringToObject(query2) : query2;
  return objectToQueryString({ ...obj1, ...obj2 }, questionMark);
}

/**
 * The shared config object to get the baseUrl for WebsiteRoutes and LearnerRoutes
 */
export interface RouteConfigOptions {
  /**
   * The hostName of the review app. Only required if you want the link to be correct when running in review apps
   */
  appHostName?: string;
  locale?: string;
}

export function getWebsiteHostForEnv(appHostName: string | undefined): string {
  if (OsPlatform.isProduction) {
    return "https://outschool.com";
  } else if (OsPlatform.isStaging) {
    return "https://staging.outschool.dev";
  } else if (appHostName) {
    return `https://${appHostName}`;
  } else {
    return "http://localhost:3000";
  }
}

export function getLearnerHostForEnv(appHostName: string | undefined): string {
  if (OsPlatform.isProduction) {
    return "https://learner.outschool.com";
  } else if (OsPlatform.isStaging) {
    return "https://learner-staging.outschool.dev";
  } else if (appHostName) {
    return `https://${appHostName}`;
  } else {
    return "http://localhost:3001";
  }
}

// See http://stackoverflow.com/questions/7905929/how-to-test-valid-uuid-guid
const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function getIdFromSlug(slug: string) {
  if (UUID_REGEX.test(slug)) {
    return slug;
  } else {
    const tokens = slug.split("-");
    if (tokens.length) {
      return tokens[tokens.length - 1];
    }
    return slug;
  }
}

export interface PathFromLocationArg {
  pathname: string;
  search: string;
}

export function pathFromLocation(location: PathFromLocationArg) {
  return !!location && location.pathname + location.search;
}

export function removeParamsFromUrl(url: string, params: string[]) {
  const urlParams = queryStringToObject(hrefToQueryString(url));
  const filteredParams = omit(urlParams, params);
  const hasParams =
    // @ts-ignore TS7
    Object.keys(filteredParams).filter((k: keyof typeof filteredParams) =>
      Boolean(filteredParams[k]),
    ).length > 0;
  if (!hasParams) {
    return url.split("?")[0];
  }
  return url.split("?")[0] + "?" + objectToQueryString(filteredParams);
}

export interface UpdateQueryParamLocation {
  pathname: string;
  query: Record<string, string | string[]>;
}
export function updateQueryParam(
  location: UpdateQueryParamLocation,
  key: string,
  value: string,
) {
  return {
    pathname: location.pathname,
    query: Object.assign({}, location.query, {
      [key]: value,
    }),
  };
}

export function slugifyTitle(title: string) {
  if (title) {
    return title
      .toLowerCase()
      .replace(/\([^)]*\)/g, "")
      .replace(/[!'",:.&]/g, "")
      .replace(/[\s]/g, "-")
      .replace(/[-]+/g, "-")
      .replace(/-$/g, "");
  } else {
    return null;
  }
}

export function makeLocalePrefix(locale?: I18nLocale | string | null) {
  return locale && locale !== "en" ? `/${locale}` : "";
}

export function pathnameWithoutLocale(locationPathname: string): string {
  for (const locale of SUPPORTED_LOCALES) {
    if (
      locationPathname.startsWith(`/${locale}/`) ||
      locationPathname === `/${locale}`
    ) {
      return locationPathname.replace(`/${locale}`, "");
    }
  }
  return locationPathname;
}

export function urlSearchParamsToObject(
  searchParamString: string,
): Record<string, string> {
  const entries = new URLSearchParams(searchParamString).entries();
  const result: Record<string, string> = {};
  for (const [key, value] of entries) {
    // each 'entry' is a [key, value] tupple
    result[key] = value;
  }
  return result;
}
